/**
 * Issues:
 * https://github.com/framer/motion/issues/940
 */

import { logger } from '@modules/logger';
import { motion } from 'motion/react';
import { type ReactNode, createContext, memo, useContext } from 'react';

import styles from './Tabs.module.scss';

interface TabsContextValues {
  onClick: (id: string) => void;
}

// Without this it gets a bit annoying to supply a default disabled option that is initially selected.
// You need to set the default value to "" which is wierd. Also this may be better from an ARIA perspective
const TabsContext = createContext<TabsContextValues>({
  onClick: () => logger.warn('onClick not implemented in TabsContext'),
});

interface TabsProp {
  onClick: (id: string) => void;
}

const Tabs: React.FC<React.PropsWithChildren<TabsProp>> = memo(({ children, onClick }) => {
  return (
    <div className={styles['tabs']}>
      <TabsContext.Provider value={{ onClick }}>
        <ul className={styles['tabs-list']}>{children}</ul>
      </TabsContext.Provider>
    </div>
  );
});

Tabs.displayName = 'Tabs';

interface TabProps {
  id: string;
  name?: string;
  selected: boolean;
  icon?: ReactNode;
}

const Tab: React.FC<React.PropsWithChildren<TabProps>> = ({ id, name, selected, icon }) => {
  const { onClick } = useContext(TabsContext);
  return (
    <li className={styles['tab']}>
      <button className={styles['tab-btn']} onClick={() => onClick(id)}>
        {name}
        {icon}
      </button>
      {selected && <motion.div layoutId="underline" className={styles['underline']} />}
    </li>
  );
};

export default Tabs;
export { Tab };
